import request from 'browser-request'
import dis from 'matrix-react-sdk/lib/dispatcher'
import { _t } from 'matrix-react-sdk/lib/languageHandler'

import url from 'url'
import UAParser from 'ua-parser-js'
import VectorBasePlatform, { updateCheckStatusEnum } from './VectorBasePlatform'

const POKE_RATE_MS = 10 * 60 * 1000 // 10 min

export default class WebPlatform extends VectorBasePlatform {
  constructor() {
    super()
    this.runningVersion = null
  }

  getHumanReadableName() {
    return 'Web Platform' // no translation required: only used for analytics
  }

  /**
   * Returns true if the platform supports displaying
   * notifications, otherwise false.
   */
  supportsNotifications() {
    return Boolean(global.Notification)
  }

  /**
   * Returns true if the application currently has permission
   * to display notifications. Otherwise false.
   */
  maySendNotifications() {
    return global.Notification.permission === 'granted'
  }

  /**
   * Requests permission to send notifications. Returns
   * a promise that is resolved when the user has responded
   * to the request. The promise has a single string argument
   * that is 'granted' if the user allowed the request or
   * 'denied' otherwise.
   */
  requestNotificationPermission() {
    // annoyingly, the latest spec says this returns a
    // promise, but this is only supported in Chrome 46
    // and Firefox 47, so adapt the callback API.
    return new Promise(resolve => {
      global.Notification.requestPermission(result => {
        resolve(result)
      })
    })
  }

  displayNotification(title, msg, avatarUrl, room) {
    const notifBody = {
      body: msg,
      tag: 'vector',
      silent: true, // we play our own sounds
    }
    if (avatarUrl) notifBody.icon = avatarUrl
    const notification = new global.Notification(title, notifBody)

    notification.onclick = () => {
      dis.dispatch({
        action: 'view_room',
        room_id: room.roomId,
      })
      global.focus()
      notification.close()
    }

    // Chrome only dismisses notifications after 20s, which
    // is waaaaay too long
    global.setTimeout(() => {
      notification.close()
    }, 5 * 1000)
  }

  //  This is a matrix interface declaration, so it needs to have _getVersion
  //  eslint-disable-next-line no-underscore-dangle
  _getVersion() {
    // We add a cachebuster to the request to make sure that we know about
    // the most recent version on the origin server. That might not
    // actually be the version we'd get on a reload (particularly in the
    // presence of intermediate caching proxies), but still: we're trying
    // to tell the user that there is a new version.

    return new Promise((resolve, reject) => {
      request(
        {
          method: 'GET',
          url: '/api/version',
          qs: { cachebuster: Date.now() },
        },
        (error, response, body) => {
          let err = error
          if (err || response.status < 200 || response.status >= 300) {
            if (err === null) err = { status: response.status }
            reject(err)
            return
          }

          const ver = body.trim()
          resolve(ver)
        }
      )
    })
  }

  getAppVersion() {
    if (this.runningVersion !== null) {
      return Promise.resolve(this.runningVersion)
    }
    //  This is a matrix interface declaration, so it needs to have _getVersion
    //  eslint-disable-next-line no-underscore-dangle
    return this._getVersion()
  }

  startUpdater() {
    this.pollForUpdate()
    setInterval(this.pollForUpdate.bind(this), POKE_RATE_MS)
  }

  async canSelfUpdate() {
    return true
  }

  pollForUpdate() {
    //  This is a matrix interface declaration, so it needs to have _getVersion
    //  eslint-disable-next-line no-underscore-dangle
    return this._getVersion().then(
      ver => {
        if (this.runningVersion === null) {
          this.runningVersion = ver
        } else if (this.runningVersion !== ver) {
          dis.dispatch({
            action: 'new_version',
            currentVersion: this.runningVersion,
            newVersion: ver,
          })
          // Return to skip a MatrixChat state update
          return null
        }
        return { status: updateCheckStatusEnum.NOTAVAILABLE }
      },
      err => {
        //  eslint-disable-next-line no-console
        console.error('Failed to poll for update', err)
        return {
          status: updateCheckStatusEnum.ERROR,
          detail:
            err.message || err.status ? err.status.toString() : 'Unknown Error',
        }
      }
    )
  }

  startUpdateCheck() {
    if (this.showUpdateCheck) return
    super.startUpdateCheck()
    this.pollForUpdate().then(updateState => {
      if (!this.showUpdateCheck) return
      if (!updateState) return
      dis.dispatch({
        action: 'check_updates',
        value: updateState,
      })
    })
  }

  installUpdate() {
    window.location.reload(true)
  }

  getDefaultDeviceDisplayName() {
    // strip query-string and fragment from uri
    const u = url.parse(window.location.href)
    u.search = ''
    u.hash = ''
    const appName = u.format()

    const ua = new UAParser()
    const browserName = ua.getBrowser().name || 'unknown browser'
    const osName = ua.getOS().name || 'unknown os'
    return _t('%(appName)s via %(browserName)s on %(osName)s', {
      appName,
      browserName,
      osName,
    })
  }

  screenCaptureErrorString() {
    // it won't work at all if you're not on HTTPS so whine whine whine
    if (!global.window || global.window.location.protocol !== 'https:') {
      return _t('You need to be using HTTPS to place a screen-sharing call.')
    }
    return null
  }

  reload() {
    // forceReload=false since we don't really need new HTML/JS files
    // we just need to restart the JS runtime.
    window.location.reload(false)
  }
}
